import { type Optional } from 'utility-types';
import {
  OfferCategory,
  OfferCtaDiscountOption,
  OfferCtaSubcategory,
} from '@bbk/retention-sdk/contracts/offer';
import { type PLANS } from '@bbkAdminRedux/rtkq/checkout.plans';
import { type RestFunnelPlacement } from '@bbkAdminRedux/rtkq/placements.utils';
import { type RestLossAversionCardOrModalCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import { type EnumLike, type Prettify } from '@bbkAdminUtils/utility-types';

export {
  OfferCategory,
  OfferCtaSubcategory,
  OfferCtaDiscountOption,
} from '@bbk/retention-sdk/contracts/offer';

export enum PublishState {
  draft = 'draft',
  live = 'live',
}

export enum OfferObjective {
  upsell = 'UPSELL',
  churn_prevention = 'CHURN_PREVENTION',
  churn_deflection = 'CHURN_DEFLECT',
  acquisition = 'ACQUISITION',
  retention = 'RETENTION',
}

export enum OfferPresentation {
  // this will be removed
  image_modal = 'image_modal',
  modal = 'modal',
  pop_up = 'pop_up',
  card = 'card',
  banner = 'banner',
  pricing_page = 'pricing_page',
}

export const GenAiOfferCategories = [
  OfferCategory.discount,
  OfferCategory.extension,
  OfferCategory.pause,
  OfferCategory.plan_change,
  OfferCategory.multi_action,
  OfferCategory.trial_conversion,
];

export enum OfferGroup {
  offer = 'offer',
  lifecycle_offer = 'lifecycle_offer',
  loss_version = 'loss_version',
}

export enum OfferContentType {
  // discounts
  dollar = 'dollar',
  precent = 'precent',
  discount_free_credits = 'discount_free_credits',
  name_your_price = 'name_your_price',
  custom = 'custom',

  // pauses
  pause_free_credits = 'pause_free_credits',
  pause_plan = 'pause_plan',
  pause_3_months = 'pause_3_months',
  pause_2_months = 'pause_2_months',
  pause_1_month = 'pause_1_month',
  skip = 'skip',
  archive_plan = 'archive_plan',
  custom_pause = 'custom_pause',

  // gifts_and_credits
  gift = 'gift',
  credits = 'credits',
  one_time_charge = 'one_time_charge',

  multi_cta = 'multi_cta',
  multi_action = 'multi_action',

  // plan changes
  upgrade = 'upgrade',
  swap_products = 'swap_products',
  downgrade = 'downgrade',
  switch_to_monthly = 'switch_to_monthly',
  trial_conversion_to_new = 'trial_conversion_to_new',
  trial_conversion_to_current = 'trial_conversion_to_current',

  new_plan = 'new_plan',
  current_plan = 'current_plan',

  // extensions
  free_months = 'free_months',
  extend_renewal_dates = 'extend_renewal_dates',
  extend_trial = 'extend_trial',

  // support & training
  chat = 'chat',
  content = 'content',
  free_training = 'free_training',
  kb_article = 'kb_article',
  onboarding = 'onboarding',
  schedule_time = 'schedule_time',
  send_email = 'send_email',
  video = 'video',

  // customer feedback
  customer_service = 'customer_service',
  general_feedback = 'general_feedback',
  product_feedback = 'product_feedback',
  sorry = 'sorry',

  // loss aversion types
  account_activity = 'account_activity',
  feature_list = 'feature_list',
  messaging = 'messaging',
  image = 'image',
  tweet = 'tweet',

  other = 'other',
}

export const TRIAL_CONVERSION_SUB_CATEGORIES = [
  OfferCtaSubcategory.trial_conversion_to_current,
  OfferCtaSubcategory.trial_conversion_to_new,
];

export const ContentTypeToSubcategoryMap: Partial<
  Record<OfferCtaSubcategory, string>
> = {
  [OfferCtaSubcategory.extend_renewal_date]: 'extend_renewal_dates',
  [OfferCtaSubcategory.swap_product]: 'swap_products',
};

export const OfferCtaSubcategoryDisplayName: Record<
  EnumLike<typeof OfferCtaSubcategory>,
  string
> = {
  [OfferCtaSubcategory.extend_renewal_date]: 'Extend Renewal Dates',
  [OfferCtaSubcategory.extend_trial]: 'Extend Trial',
  [OfferCtaSubcategory.upgrade]: 'Upgrade',
  [OfferCtaSubcategory.downgrade]: 'Downgrade',
  [OfferCtaSubcategory.swap_product]: 'Swap Products',
  [OfferCtaSubcategory.add_on]: 'Addon',
  [OfferCtaSubcategory.gift]: 'Gift',
  [OfferCtaSubcategory.credit]: 'Credits',
  [OfferCtaSubcategory.chat]: 'Chat',
  [OfferCtaSubcategory.content]: 'Content',
  [OfferCtaSubcategory.free_training]: 'Free Training',
  [OfferCtaSubcategory.kb_article]: 'KB article',
  [OfferCtaSubcategory.onboarding]: 'Onboarding',
  [OfferCtaSubcategory.schedule_time]: 'Schedule Time',
  [OfferCtaSubcategory.send_email]: 'Send Email',
  [OfferCtaSubcategory.video]: 'Video',
  [OfferCtaSubcategory.customer_service]: 'Customer Service',
  [OfferCtaSubcategory.general_feedback]: 'General Feedback',
  [OfferCtaSubcategory.product_feedback]: 'Product Feedback',
  [OfferCtaSubcategory.sorry]: 'Sorry',
  [OfferCtaSubcategory.pause_plan]: 'Pause Plan',
  [OfferCtaSubcategory.precent]: 'Discount (%)',
  [OfferCtaSubcategory.dollar]: 'Discount ($)',
  [OfferCtaSubcategory.trial_conversion_to_current]:
    'Trial Conversion to Current Plan',
  [OfferCtaSubcategory.trial_conversion_to_new]: 'Trial Conversion to New Plan',
  [OfferCtaSubcategory.new_plan]: 'Trial Conversion to New Plan',
  [OfferCtaSubcategory.current_plan]: 'Trial Conversion to Current Plan',
};

const OfferCtaCategory: Partial<
  Record<
    EnumLike<typeof OfferCategory>,
    EnumLike<typeof OfferCtaSubcategory>[] | undefined
  >
> = {
  [OfferCategory.discount]: undefined,
  [OfferCategory.pause]: undefined,
  [OfferCategory.extension]: [
    OfferCtaSubcategory.extend_renewal_date,
    OfferCtaSubcategory.extend_trial,
  ],
  [OfferCategory.plan_change]: [
    OfferCtaSubcategory.upgrade,
    OfferCtaSubcategory.downgrade,
    OfferCtaSubcategory.swap_product,
    OfferCtaSubcategory.add_on,
  ],
  [OfferCategory.gifts_and_credits]: [
    OfferCtaSubcategory.gift,
    OfferCtaSubcategory.credit,
  ],
  [OfferCategory.one_time_charge]: undefined,
  [OfferCategory.support]: [
    OfferCtaSubcategory.chat,
    OfferCtaSubcategory.content,
    OfferCtaSubcategory.free_training,
    OfferCtaSubcategory.kb_article,
    OfferCtaSubcategory.onboarding,
    OfferCtaSubcategory.schedule_time,
    OfferCtaSubcategory.send_email,
    OfferCtaSubcategory.video,
  ],
  [OfferCategory.customer_feedback]: [
    OfferCtaSubcategory.customer_service,
    OfferCtaSubcategory.general_feedback,
    OfferCtaSubcategory.product_feedback,
    OfferCtaSubcategory.sorry,
  ],
  [OfferCategory.trial_conversion]: [
    OfferCtaSubcategory.new_plan,
    OfferCtaSubcategory.current_plan,
  ],
  [OfferCategory.other]: undefined,
};

export const getOfferSubcategories = (
  cat: EnumLike<typeof OfferCategory>,
  filter?: (subCat: EnumLike<typeof OfferCtaSubcategory>) => boolean
) => OfferCtaCategory[cat]?.filter(filter ?? (() => true));

export const OfferCtaDiscountOptionDisplayName: Record<
  OfferCtaDiscountOption,
  string
> = {
  [OfferCtaDiscountOption.fixed]: '$ based discount',
  [OfferCtaDiscountOption.percent]: '% based discount',
  [OfferCtaDiscountOption.quantity]: 'Quantity based discount',
};

type BillingOfferKeys = {
  coupon_id?: string;
  plan_id?: string;
  interval_count?: number;
  billing_interval?: 'day' | 'week' | 'month' | 'year';
};

enum TagPrefixes {
  plans = 'plans',
}

type Tags = `${TagPrefixes.plans}:${PLANS}`[];

type MaxRedemption = {
  max_redemption_count: number;
};

export type PlayOfferPlacement = {
  content_id: string;
  play_display_name: string;
  play_id: string;
};

export type RestOffer = {
  active: boolean;
  id: string;
  display_name: string;
  internal_name: string;
  published_at?: string;
  updated_at?: string;
  created_at?: string;
  category: EnumLike<typeof OfferCategory>;
  type: EnumLike<typeof OfferContentType>;
  primary_card_name: string;
  groups: EnumLike<typeof OfferGroup>[];
  cards: RestLossAversionCardOrModalCard[];
  tags?: Tags;
  placements: RestFunnelPlacement[];
  play_placements: PlayOfferPlacement[];
  objective: EnumLike<typeof OfferObjective>;
  presentation: EnumLike<typeof OfferPresentation>;
  brand_id?: string;
  shared: boolean;
  /** Enabled locale codes (base first). */
  enabled_locales?: string[];
} & BillingOfferKeys &
  MaxRedemption;

export type NewOffer = RestOffer & {
  isNew: boolean;
  id: string;
};

export const isNewOffer = (offer: RestOffer | NewOffer): offer is NewOffer =>
  (offer as NewOffer).isNew;

export const isChurnDeflectOrRetention = (
  offer: RestOffer | NewOffer
): boolean => {
  return (
    offer.objective === OfferObjective.churn_deflection ||
    (offer.objective === OfferObjective.retention &&
      offer.presentation === OfferPresentation.modal)
  );
};

export const isNewLossAversion = (
  lossAversion: RestLossAversion | NewLossAversionTemplate
): lossAversion is NewLossAversionTemplate =>
  (lossAversion as NewLossAversionTemplate).isNew;

export type RestLossAversion = RestOffer;

export type NewLossAversionTemplate = RestLossAversion & {
  isNew: true;
};

export type CreateLossAversionDto = Prettify<
  Optional<NewLossAversionTemplate, 'id' | 'isNew'>
>;
